/**
 * Shopify Cart Temperature Validation
 * 
 * Prevents mixing 'hot' and 'cold' tagged products in the cart.
 * Add this to your theme's assets folder and include it in theme.liquid,
 * or paste directly into your cart.liquid / cart-drawer snippet.
 *
 * Usage:
 *   <script src="{{ 'cart-temperature-validation.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  const HOT_TAG = 'postal';
  const COLD_TAG = 'central';

  /**
   * Determines the temperature type of a product by its tags array.
   * Returns 'hot', 'cold', or null.
   */
  function getTemperatureType(tags) {
    if (!Array.isArray(tags)) {
      // tags may be a comma-separated string in some contexts
      tags = tags ? tags.split(',').map(t => t.trim().toLowerCase()) : [];
    } else {
      tags = tags.map(t => t.trim().toLowerCase());
    }
    if (tags.includes(HOT_TAG)) return HOT_TAG;
    if (tags.includes(COLD_TAG)) return COLD_TAG;
    return null;
  }

  /**
   * Fetches the full cart and checks for temperature conflicts.
   * Returns an object: { conflict: bool, existingType: string|null }
   */
  async function getCartTemperatureState() {
    const response = await fetch('/cart.js');
    const cart = await response.json();

    let detectedType = null;

    for (const item of cart.items) {
      const itemType = getTemperatureType(item.tags || []);
      if (itemType) {
        detectedType = itemType;
        break; // We only need to know what's already in the cart
      }
    }

    return { existingType: detectedType, itemCount: cart.item_count };
  }

  /**
   * Fetches product tags by variant ID using the Storefront.
   */
  async function getProductTagsByVariantId(variantId) {
    // Use the /products.json or product handle approach;
    // here we use the cart add endpoint's product_id field by first
    // looking up the variant via the /variants endpoint.
    const res = await fetch(`/variants/${variantId}.js`);
    if (!res.ok) return [];
    const variant = await res.json();

    const productRes = await fetch(`/products/${variant.product_id}.js`);
    if (!productRes.ok) return [];
    const product = await productRes.json();

    return product.tags || [];
  }

  /**
   * Shows a user-friendly error banner on the page.
   */
  function showTemperatureError(existingType, newType) {
    // Remove any existing banner
    const existing = document.getElementById('temp-conflict-banner');
    if (existing) existing.remove();

    const banner = document.createElement('div');
    banner.id = 'temp-conflict-banner';
    banner.setAttribute('role', 'alert');
    banner.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 99999;
      background: #fff3cd;
      border: 2px solid #ffc107;
      border-radius: 8px;
      padding: 16px 24px;
      max-width: 480px;
      width: 90%;
      box-shadow: 0 4px 16px rgba(0,0,0,0.15);
      font-family: inherit;
      font-size: 14px;
      color: #856404;
      text-align: center;
    `;

    const existingLabel = existingType === HOT_TAG ? '📮 postal' : '🏙️ central';
    const newLabel = newType === HOT_TAG ? '📮 postal' : '🏙️ central';

    banner.innerHTML = `
      <strong>Can't mix postal &amp; central items</strong><br>
      Your cart already contains ${existingLabel} items.<br>
      Please remove them before adding ${newLabel} items.
      <br><br>
      <button onclick="document.getElementById('temp-conflict-banner').remove()"
        style="background:#ffc107;border:none;padding:6px 16px;border-radius:4px;cursor:pointer;font-weight:bold;color:#333;">
        OK
      </button>
    `;

    document.body.appendChild(banner);

    // Auto-dismiss after 6 seconds
    setTimeout(() => {
      if (document.getElementById('temp-conflict-banner')) {
        banner.remove();
      }
    }, 6000);
  }

  /**
   * Intercepts the native Shopify /cart/add.js fetch/XHR requests
   * and validates temperature before allowing them through.
   *
   * Works by wrapping window.fetch.
   */
  const originalFetch = window.fetch;

  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : input?.url;

    // Only intercept cart/add requests
    if (url && url.includes('/cart/add')) {
      try {
        let variantId = null;

        // Parse body to find the variant id
        if (init?.body) {
          let body = init.body;

          if (typeof body === 'string') {
            // JSON body
            try {
              const parsed = JSON.parse(body);
              variantId = parsed.id || parsed.items?.[0]?.id;
            } catch {
              // FormData-encoded string
              const params = new URLSearchParams(body);
              variantId = params.get('id');
            }
          } else if (body instanceof FormData) {
            variantId = body.get('id');
          } else if (body instanceof URLSearchParams) {
            variantId = body.get('id');
          }
        }

        if (variantId) {
          const [tags, cartState] = await Promise.all([
            getProductTagsByVariantId(variantId),
            getCartTemperatureState()
          ]);

          const newType = getTemperatureType(tags);
          const existingType = cartState.existingType;

          // Conflict: cart has one temperature type, new item has the opposite
          if (
            newType &&
            existingType &&
            newType !== existingType
          ) {
            showTemperatureError(existingType, newType);

            // Return a fake failed Response so the add is blocked
            return new Response(
              JSON.stringify({
                status: 422,
                message: `Cannot add ${newType} items to a cart containing ${existingType} items.`,
                description: `Your cart only allows one temperature type at a time.`
              }),
              {
                status: 422,
                headers: { 'Content-Type': 'application/json' }
              }
            );
          }
        }
      } catch (err) {
        console.warn('[TempValidation] Error during pre-check:', err);
        // On error, allow the request through (fail open)
      }
    }

    return originalFetch.apply(this, arguments);
  };

  /**
   * Also intercept XMLHttpRequest for themes that use XHR instead of fetch.
   */
  const OriginalXHR = window.XMLHttpRequest;

  function PatchedXHR() {
    const xhr = new OriginalXHR();
    const originalOpen = xhr.open.bind(xhr);
    const originalSend = xhr.send.bind(xhr);

    let requestUrl = '';

    xhr.open = function (method, url, ...args) {
      requestUrl = url;
      return originalOpen(method, url, ...args);
    };

    xhr.send = async function (body) {
      if (requestUrl.includes('/cart/add')) {
        try {
          let variantId = null;

          if (typeof body === 'string') {
            try {
              const parsed = JSON.parse(body);
              variantId = parsed.id || parsed.items?.[0]?.id;
            } catch {
              const params = new URLSearchParams(body);
              variantId = params.get('id');
            }
          } else if (body instanceof FormData) {
            variantId = body.get('id');
          }

          if (variantId) {
            const [tags, cartState] = await Promise.all([
              getProductTagsByVariantId(variantId),
              getCartTemperatureState()
            ]);

            const newType = getTemperatureType(tags);
            const existingType = cartState.existingType;

            if (newType && existingType && newType !== existingType) {
              showTemperatureError(existingType, newType);

              // Simulate a 422 response
              Object.defineProperty(xhr, 'status', { get: () => 422 });
              Object.defineProperty(xhr, 'responseText', {
                get: () => JSON.stringify({
                  status: 422,
                  message: `Cannot mix postal and central items in the cart.`
                })
              });
              xhr.dispatchEvent(new Event('load'));
              return;
            }
          }
        } catch (err) {
          console.warn('[TempValidation] XHR pre-check error:', err);
        }
      }

      return originalSend(body);
    };

    return xhr;
  }

  PatchedXHR.prototype = OriginalXHR.prototype;
  window.XMLHttpRequest = PatchedXHR;

  console.log('[TempValidation] Cart temperature validation active.');
})();